import React from "react";
import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatNumber } from "@/utils/numberUtils";
import { EstimateCreditsDetailResponse } from "@/apis/exportTrackingApi";
import { CREDITS_BREAKDOWN_LABELS } from "./creditsBreakdownLabels";

interface CreditsBreakdownTableProps {
  details: EstimateCreditsDetailResponse["details"];
  sx?: SxProps;
}

export const CreditsBreakdownTable: React.FC<CreditsBreakdownTableProps> = ({
  details,
  sx,
}) => {
  const rows = Object.entries(details).filter(
    ([, value]) => value.counts !== 0 || value.credits !== 0
  );

  if (rows.length === 0) return null;

  // "Count" is the full selection while "Will Export" is the slice the export size caps
  // let through. They only need separating once a cap actually bites -- otherwise the
  // second column would just repeat the first (covered wagon exports apply no caps).
  const isCapped = rows.some(
    ([, value]) =>
      typeof value.export_counts === "number" &&
      value.export_counts < value.counts
  );

  return (
    <>
      <TableContainer
        data-testid="export-credits-breakdown-table"
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          ...sx,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Count
              </TableCell>
              {isCapped && (
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Will Export
                </TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Credits
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([key, value]) => (
              <TableRow
                key={key}
                data-testid={`export-credits-breakdown-row-${key}`}
              >
                <TableCell>{CREDITS_BREAKDOWN_LABELS[key] ?? key}</TableCell>
                <TableCell align="right">
                  {formatNumber(value.counts)}
                </TableCell>
                {isCapped && (
                  <TableCell
                    align="right"
                    data-testid={`export-credits-breakdown-export-count-${key}`}
                  >
                    {typeof value.export_counts === "number"
                      ? formatNumber(value.export_counts)
                      : "—"}
                  </TableCell>
                )}
                <TableCell align="right">
                  {formatNumber(value.credits)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isCapped && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          data-testid="export-credits-breakdown-cap-note"
          sx={{ mt: 0.5 }}
        >
          Count is the full size of your selection. Will Export is what this
          export delivers after its size limits, and credits are charged on that
          figure only.
        </Typography>
      )}
    </>
  );
};
